// Bottom-center scroll affordance ("Scroll down to start generating...").
// Shown while the target element (TTSView's block/generate column) has
// never been sufficiently visible in the viewport — first-time talents on
// small screens otherwise miss that the generation UI sits below the
// video/script area. Dismisses itself permanently (per mount) the first
// time the target scrolls into view; clicking scrolls it into view.
//
// Horizontal position: centered on the CONTENT pane (<main> inside the
// AppShell contentScroll), not the viewport — with the asset sidebar open
// the two centers differ noticeably. Measured on show + resize.

import { useEffect, useState } from 'react';
import styles from './ScrollHint.module.css';

interface Props {
  /** Element the hint points at. Null until the parent's ref attaches —
   *  the observer starts on the next render that passes a real element. */
  targetEl: HTMLElement | null;
  label: string;
}

export function ScrollHint({ targetEl, label }: Props) {
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [leftPx, setLeftPx] = useState<number | null>(null);

  useEffect(() => {
    if (!targetEl || dismissed) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Seen once → gone for good (per mount). Re-showing on every
          // scroll-up would nag users who deliberately went back to the
          // video/script area.
          setDismissed(true);
          setVisible(false);
        } else {
          setVisible(true);
        }
      },
      // Count as "seen" only once ~120px of the target is inside the
      // viewport — a 1px sliver at the fold shouldn't dismiss the hint.
      { rootMargin: '0px 0px -120px 0px', threshold: 0 },
    );
    io.observe(targetEl);
    return () => io.disconnect();
  }, [targetEl, dismissed]);

  // Center on the content pane. <main> is horizontally centered inside the
  // scroll pane, so its center == pane center (minus scrollbar gutter).
  useEffect(() => {
    if (!targetEl || !visible) return;
    const pane = targetEl.closest('main');
    const update = () => {
      const r = (pane ?? document.body).getBoundingClientRect();
      setLeftPx(r.left + r.width / 2);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [targetEl, visible]);

  if (dismissed || !visible) return null;
  return (
    <button
      type="button"
      className={styles.hint}
      style={leftPx !== null ? { left: leftPx } : undefined}
      onClick={() =>
        targetEl?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    >
      <i className="ph-bold ph-caret-down" aria-hidden="true" />
      {label}
    </button>
  );
}
